import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { 
    Box, Typography, Container, Paper, Tabs, Tab, 
    CircularProgress, Alert, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Grid, Card, CardContent,
    Chip, Divider
} from '@mui/material';
import { 
    FaTrophy, FaCalendarAlt, FaInfoCircle, FaHandshake, 
    FaQrcode, FaMapMarkerAlt, FaClock, FaCalendarDay
} from 'react-icons/fa';
import dayjs from 'dayjs';

const TournamentLive = () => {
    const { id } = useParams();
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const fetchTournamentData = useCallback(() => {
        axios.get(`${API_BASE_URL}/api/tournaments/public/${id}`)
            .then(res => {
                setTournament(res.data);
                setError(null);
            })
            .catch(err => {
                console.error("Error fetching public tournament data", err);
                setError("Turnier konnte nicht geladen werden oder existiert nicht.");
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        fetchTournamentData();
        // Auto-refresh every 30 seconds for live results
        const interval = setInterval(fetchTournamentData, 30000);
        return () => clearInterval(interval);
    }, [fetchTournamentData]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !tournament) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">{error || "Turnier wurde nicht gefunden."}</Alert>
            </Container>
        );
    }

    // Dynamic calculations for standings
    const calculateStandings = (groupName) => {
        const group = tournament.groups?.find(g => g.name === groupName);
        if (!group) return [];

        const teamStats = {};
        group.teams.forEach(team => {
            teamStats[team] = {
                team,
                played: 0,
                won: 0,
                drawn: 0,
                lost: 0,
                goalsFor: 0,
                goalsAgainst: 0,
                points: 0
            };
        });

        // Filter completed matches of this group
        const groupMatches = tournament.matches?.filter(m => 
            m.group === groupName && m.status === 'Completed' && 
            m.scoreA !== null && m.scoreB !== null
        ) || [];

        groupMatches.forEach(m => {
            const statsA = teamStats[m.teamA];
            const statsB = teamStats[m.teamB];

            if (statsA && statsB) {
                statsA.played += 1;
                statsB.played += 1;
                statsA.goalsFor += m.scoreA;
                statsA.goalsAgainst += m.scoreB;
                statsB.goalsFor += m.scoreB;
                statsB.goalsAgainst += m.scoreA;

                if (m.scoreA > m.scoreB) {
                    statsA.won += 1;
                    statsA.points += 3;
                    statsB.lost += 1;
                } else if (m.scoreA < m.scoreB) {
                    statsB.won += 1;
                    statsB.points += 3;
                    statsA.lost += 1;
                } else {
                    statsA.drawn += 1;
                    statsA.points += 1;
                    statsB.drawn += 1;
                    statsB.points += 1;
                }
            }
        });

        return Object.values(teamStats).sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points;
            const diffA = a.goalsFor - a.goalsAgainst;
            const diffB = b.goalsFor - b.goalsAgainst;
            if (diffB !== diffA) return diffB - diffA;
            if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
            return a.team.localeCompare(b.team);
        });
    };

    const shareUrl = window.location.href;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(shareUrl)}`;

    return (
        <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
            {/* Header Banner */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: { xs: 3, md: 4 }, 
                    mb: 3, 
                    background: 'linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)',
                    color: 'white',
                    borderRadius: '16px',
                    textAlign: 'center',
                    boxShadow: '0 8px 32px rgba(211, 47, 47, 0.2)'
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                    {tournament.name}
                </Typography>
                
                <Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
                    <Chip 
                        icon={<FaCalendarDay style={{ color: 'white' }} />} 
                        label={dayjs(tournament.date).format('DD. MMMM YYYY')} 
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} 
                    />
                    {tournament.location && (
                        <Chip 
                            icon={<FaMapMarkerAlt style={{ color: 'white' }} />} 
                            label={tournament.location} 
                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} 
                        />
                    )}
                    <Chip 
                        label="LIVE-ERGEBNISSE" 
                        color="success" 
                        variant="filled" 
                        sx={{ fontWeight: 'bold', animation: 'pulse 2s infinite' }}
                    />
                </Box>

                {tournament.description && (
                    <Typography variant="body1" sx={{ mt: 3, opacity: 0.9, maxWidth: '600px', mx: 'auto', fontStyle: 'italic' }}>
                        {tournament.description}
                    </Typography>
                )}
            </Paper>

            {/* Navigation Tabs */}
            <Paper sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="primary"
                    indicatorColor="primary"
                >
                    <Tab icon={<FaCalendarAlt />} iconPosition="start" label="Spielplan" />
                    <Tab icon={<FaTrophy />} iconPosition="start" label="Tabellen" />
                    <Tab icon={<FaInfoCircle />} iconPosition="start" label="Turnier-Info" />
                    <Tab icon={<FaHandshake />} iconPosition="start" label="Sponsoren" />
                    <Tab icon={<FaQrcode />} iconPosition="start" label="Teilen" />
                </Tabs>
            </Paper>

            {/* Tab Contents */}
            <Box>
                {/* TAB 0: SPIELPLAN */}
                {activeTab === 0 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>
                            Spielplan & Spielergebnisse
                        </Typography>
                        
                        {!tournament.matches || tournament.matches.length === 0 ? (
                            <Alert severity="info">Der Spielplan wurde noch nicht veröffentlicht.</Alert>
                        ) : (
                            tournament.matches
                                .sort((a, b) => {
                                    // Order by time chronologically
                                    return a.time.localeCompare(b.time);
                                })
                                .map((match) => (
                                    <Card 
                                        key={match._id} 
                                        sx={{ 
                                            borderRadius: '12px',
                                            borderLeft: match.status === 'Completed' ? '6px solid #2e7d32' : '6px solid #ed6c02',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.01)' }
                                        }}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Grid container alignItems="center" spacing={2}>
                                                <Grid item xs={12} sm={3}>
                                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <FaClock style={{ color: 'gray' }} />
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                {match.time} Uhr
                                                            </Typography>
                                                        </Box>
                                                        <Box display="flex" gap={1} flexWrap="wrap">
                                                            {match.group && (
                                                                <Chip label={match.group} size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />
                                                            )}
                                                            {match.court && (
                                                                <Chip label={match.court} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                                                            )}
                                                            {match.round && match.round !== "Vorrunde" && (
                                                                <Chip label={match.round} size="small" color="secondary" sx={{ height: 20, fontSize: '0.75rem' }} />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                
                                                <Grid item xs={9} sm={6}>
                                                    <Box display="flex" flexDirection="column" gap={1}>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                                            <Typography 
                                                                variant="body1" 
                                                                fontWeight={match.status === 'Completed' && match.scoreA > match.scoreB ? 'bold' : 'normal'}
                                                            >
                                                                {match.teamA}
                                                            </Typography>
                                                        </Box>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                                            <Typography 
                                                                variant="body1" 
                                                                fontWeight={match.status === 'Completed' && match.scoreB > match.scoreA ? 'bold' : 'normal'}
                                                            >
                                                                {match.teamB}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={3} sm={3}>
                                                    <Box 
                                                        display="flex" 
                                                        flexDirection="column" 
                                                        alignItems="center" 
                                                        justifyContent="center"
                                                        bgcolor="#f5f5f5"
                                                        borderRadius="8px"
                                                        py={1}
                                                    >
                                                        {match.status === 'Completed' && match.scoreA !== null && match.scoreB !== null ? (
                                                            <>
                                                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                                                    {match.scoreA} : {match.scoreB}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                                                                    Beendet
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Typography variant="h6" fontWeight="bold" color="text.secondary">
                                                                    - : -
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Geplant
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))
                        )}
                    </Box>
                )}

                {/* TAB 1: TABELLEN */}
                {activeTab === 1 && (
                    <Box display="flex" flexDirection="column" gap={4}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            Gruppentabellen
                        </Typography>

                        {!tournament.groups || tournament.groups.length === 0 ? (
                            <Alert severity="info">Es sind noch keine Gruppen eingeteilt.</Alert>
                        ) : (
                            tournament.groups.map((group) => {
                                const standings = calculateStandings(group.name);
                                return (
                                    <Paper key={group.name} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                                        <Typography variant="h6" fontWeight="bold" mb={2} color="secondary.main">
                                            {group.name}
                                        </Typography>
                                        
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                        <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Pl.</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Mannschaft</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Sp.</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>S</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>U</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>N</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>Tore</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Pkt.</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {standings.map((row, idx) => (
                                                        <TableRow key={row.team} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>{idx + 1}</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>{row.team}</TableCell>
                                                            <TableCell align="center">{row.played}</TableCell>
                                                            <TableCell align="center">{row.won}</TableCell>
                                                            <TableCell align="center">{row.drawn}</TableCell>
                                                            <TableCell align="center">{row.lost}</TableCell>
                                                            <TableCell align="center">{row.goalsFor}:{row.goalsAgainst}</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                                {row.points}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                );
                            })
                        )}
                    </Box>
                )}

                {/* TAB 2: TURNIER-INFO */}
                {activeTab === 2 && (
                    <Paper sx={{ p: 3, borderRadius: '12px' }}>
                        <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
                            Allgemeine Informationen
                        </Typography>
                        
                        <Grid container spacing={3} mb={3}>
                            <Grid item xs={12} sm={6}>
                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                    <Box bgcolor="rgba(211, 47, 47, 0.1)" color="primary.main" p={1.5} borderRadius="50%" display="flex">
                                        <FaMapMarkerAlt size={20} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" display="block">Austragungsort</Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {tournament.location || "TC Opfingen"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                    <Box bgcolor="rgba(211, 47, 47, 0.1)" color="primary.main" p={1.5} borderRadius="50%" display="flex">
                                        <FaClock size={20} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" display="block">Ansprechpartner</Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {tournament.contact || "Turnierleitung"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" fontWeight="bold" mb={1} mt={2}>
                            Regeln & Spielordnung
                        </Typography>
                        
                        {!tournament.rules ? (
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                Keine speziellen Regeln für dieses Turnier hinterlegt.
                            </Typography>
                        ) : (
                            <Typography 
                                variant="body2" 
                                component="div" 
                                sx={{ 
                                    whiteSpace: 'pre-line', 
                                    lineHeight: 1.6,
                                    color: 'text.primary',
                                    p: 2,
                                    bgcolor: '#fafafa',
                                    borderRadius: '8px'
                                }}
                            >
                                {tournament.rules}
                            </Typography>
                        )}
                    </Paper>
                )}

                {/* TAB 3: SPONSOREN */}
                {activeTab === 3 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            Unsere Turniersponsoren
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            Ein herzliches Dankeschön an all unsere Partner, die dieses Grümpelturnier möglich machen!
                        </Typography>
                        
                        {!tournament.sponsors || tournament.sponsors.length === 0 ? (
                            <Alert severity="info">Noch keine Sponsoren für dieses Turnier eingetragen.</Alert>
                        ) : (
                            <Grid container spacing={3} justifyContent="center" alignItems="center">
                                {tournament.sponsors.map((sponsor) => (
                                    <Grid item xs={6} sm={4} key={sponsor._id}>
                                        <Card 
                                            component={sponsor.website ? "a" : "div"}
                                            href={sponsor.website || undefined}
                                            target={sponsor.website ? "_blank" : undefined}
                                            rel={sponsor.website ? "noopener noreferrer" : undefined}
                                            sx={{ 
                                                p: 2, 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                height: 140,
                                                borderRadius: '12px',
                                                textDecoration: 'none',
                                                transition: 'box-shadow 0.3s, transform 0.3s',
                                                cursor: sponsor.website ? 'pointer' : 'default',
                                                '&:hover': sponsor.website ? { 
                                                    boxShadow: '0px 8px 24px rgba(0,0,0,0.1)', 
                                                    transform: 'translateY(-4px)' 
                                                } : {}
                                            }}
                                        >
                                            {sponsor.logoUrl ? (
                                                <Box 
                                                    component="img" 
                                                    src={`${API_BASE_URL}${sponsor.logoUrl}`} 
                                                    alt={sponsor.name}
                                                    sx={{ 
                                                        maxWidth: '100%', 
                                                        maxHeight: 80, 
                                                        objectFit: 'contain',
                                                        mb: 1
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{ height: 80, display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <FaHandshake size={40} style={{ color: '#ccc' }} />
                                                </Box>
                                            )}
                                            <Typography variant="subtitle2" align="center" fontWeight="bold" color="textPrimary">
                                                {sponsor.name}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                )}

                {/* TAB 4: TEILEN / QR-CODE */}
                {activeTab === 4 && (
                    <Paper sx={{ p: 4, borderRadius: '12px', textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
                            Ergebnisse & Spielplan teilen
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" mb={3} sx={{ maxWidth: '500px', mx: 'auto' }}>
                            Scanne diesen QR-Code mit deinem Smartphone, um die Live-Ergebnisse direkt mobil zu verfolgen, oder teile den Link mit deinen Teammitgliedern.
                        </Typography>
                        
                        <Box 
                            component="img" 
                            src={qrCodeUrl} 
                            alt="Turnier QR Code" 
                            sx={{ 
                                p: 2, 
                                border: '1px solid #eee', 
                                borderRadius: '12px', 
                                bgcolor: 'white',
                                mb: 3,
                                boxShadow: '0px 4px 20px rgba(0,0,0,0.05)'
                            }}
                        />

                        <Box bgcolor="#f9f9f9" p={2} borderRadius="8px" sx={{ wordBreak: 'break-all', maxWidth: '500px', mx: 'auto' }}>
                            <Typography variant="caption" color="textSecondary" display="block">Link zur Seite:</Typography>
                            <Typography 
                                component="a" 
                                href={shareUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                sx={{ fontWeight: 'bold', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {shareUrl}
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Container>
    );
};

export default TournamentLive;
